import { useState, useEffect, useCallback } from 'react';
import {
  Inspection,
  Verification,
  ClosureEvent,
  CreateInspectionDto,
  CreateVerificationDto,
} from '../types/inspection';
import { inspectionService } from '../services/inspection/inspectionService';

export function useInspection(workOrderId?: string, complaintId?: string) {
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [closureHistory, setClosureHistory] = useState<ClosureEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadInspections = useCallback(async () => {
    if (!workOrderId) return;
    setIsLoading(true);
    const res = await inspectionService.getInspections({ work_order_id: workOrderId });
    if (res.success) setInspections(res.data);
    setIsLoading(false);
  }, [workOrderId]);

  const loadVerifications = useCallback(async () => {
    if (!complaintId) return;
    const res = await inspectionService.getVerifications({ complaint_id: complaintId });
    if (res.success) setVerifications(res.data);
  }, [complaintId]);

  const loadClosureHistory = useCallback(async () => {
    if (!complaintId) return;
    const res = await inspectionService.getClosureHistory(complaintId);
    if (res.success) setClosureHistory(res.data);
  }, [complaintId]);

  useEffect(() => {
    loadInspections();
    loadVerifications();
    loadClosureHistory();
  }, [loadInspections, loadVerifications, loadClosureHistory]);

  const submitInspection = async (data: CreateInspectionDto) => {
    const res = await inspectionService.createInspection(data);
    if (res.success) {
      setInspections(prev => [res.data, ...prev]);
      await loadClosureHistory();
    }
    return res;
  };

  const submitVerification = async (data: CreateVerificationDto) => {
    const res = await inspectionService.createVerification(data);
    if (res.success) {
      setVerifications(prev => [res.data, ...prev]);
      await loadClosureHistory();
    }
    return res;
  };

  const closeComplaint = async (verificationId: string, satisfactionRating: number) => {
    if (!complaintId) return { success: false, data: null, message: 'No complaint ID' };
    const res = await inspectionService.closeComplaint(complaintId, {
      verification_id: verificationId,
      satisfaction_rating: satisfactionRating,
    });
    if (res.success) {
      await loadClosureHistory();
    }
    return res;
  };

  const latestInspection = inspections.length > 0 ? inspections[0] : null;
  const latestVerification = verifications.length > 0 ? verifications[0] : null;

  return {
    inspections,
    verifications,
    closureHistory,
    latestInspection,
    latestVerification,
    isLoading,
    submitInspection,
    submitVerification,
    closeComplaint,
    refresh: () => {
      loadInspections();
      loadVerifications();
      loadClosureHistory();
    },
  };
}
